import { useEffect, useRef, InputHTMLAttributes, RefObject } from 'react';
import { useField } from '@unform/core';

import { Container, InputElement, Header, WarningText, Label } from './styles';

interface Props {
  name: string;
  label?: string;
  options: {
    id: string;
    value: string;
    label: string;
  }[];
  defaultValue?: string;
  direction: 'row' | 'column';
  required?: boolean;
}

type RefInputEl = RefObject<HTMLInputElement[]>;

type InputProps = InputHTMLAttributes<HTMLInputElement> & Props;

const Radio: React.FC<InputProps> = ({
  name,
  label,
  options,
  direction,
  required,
  defaultValue,
  ...rest
}) => {
  const inputRefs = useRef<any>([]);
  const { fieldName, registerField, error } = useField(name);

  defaultValue = defaultValue || '';

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRefs,
      getValue: (refs: RefInputEl) => {
        return refs.current?.find(input => input?.checked)?.value || '';
      },
      setValue: (refs: RefInputEl, id: string) => {
        const inputRef = refs.current?.find(ref => ref.id === id);
        if (inputRef) inputRef.checked = true;
      },
      clearValue: (refs: RefInputEl) => {
        const inputRef = refs.current?.find(ref => ref.checked === true);
        if (inputRef) inputRef.checked = false;
      },
    });
  }, [fieldName, registerField]);

  return (
    <Container id={name} isErrored={!!error}>
      <Header>
        <Label>
          {label} {required && <b style={{ color: 'var(--danger)' }}>*</b>}
        </Label>
        {error && <WarningText>{error}</WarningText>}
      </Header>

      <div style={{ flexDirection: direction }}>
        {options.map((option, index) => (
          <span key={option.id}>
            <InputElement
              type="radio"
              ref={ref => {
                inputRefs.current[index] = ref;
              }}
              id={option.id}
              name={name}
              defaultChecked={defaultValue?.includes(option.id)}
              value={option.value}
              {...rest}
            />

            <label htmlFor={option.id} key={option.id}>
              {option.label}
            </label>
          </span>
        ))}
      </div>
    </Container>
  );
};

export default Radio;
